import React, { useState, useMemo } from 'react';
import { useApp, hasPrefix, previewDocNo } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import PrintPreview from '../common/PrintPreview';
import { Plus, Trash2, Pencil, Printer } from 'lucide-react';
import { formatDate, today } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';

const emptyLine = () => ({ itemId: '', qty: 0 });
const emptyForm = () => ({ date: today(), batchNo: '', narration: '', inputs: [emptyLine()], outputs: [emptyLine()] });

// An item counts as raw material if its (free-text) category contains "raw".
const isRawMaterial = (item) => (item.category || '').toLowerCase().includes('raw');

export default function ProductionEntry() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const [tab, setTab] = useState('entry');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printDoc, setPrintDoc] = useState(null);

  const getItem = (id) => state.items.find(i => i.id === id);
  const itemName = (id) => getItem(id)?.name || '-';
  const itemStock = (id) => getItem(id)?.currentStock || 0;

  // Input dropdown shows raw-material items only; outputs show all active items.
  const rawItems = state.items.filter(i => i.isActive !== false && isRawMaterial(i));
  const outputItems = state.items.filter(i => i.isActive !== false);

  // When editing, the old consumption is reversed on save, so that item's own qty is available again.
  const availableFor = (itemId) => {
    let avail = itemStock(itemId);
    if (editId) {
      const old = state.productions.find(p => p.id === editId);
      (old?.inputs || []).forEach(li => { if (li.itemId === itemId) avail += Number(li.qty) || 0; });
      (old?.outputs || []).forEach(li => { if (li.itemId === itemId) avail -= Number(li.qty) || 0; });
    }
    return avail;
  };

  const errors = useMemo(() => {
    const rules = { date: [validators.required(form.date, 'Date')] };
    const validLines = (lines) => lines.filter(l => l.itemId && Number(l.qty) > 0);
    if (validLines(form.inputs).length === 0) rules.inputs = ['Add at least one input (raw material) item'];
    if (validLines(form.outputs).length === 0) rules.outputs = ['Add at least one output (finished) item'];
    // Stock sufficiency for inputs
    form.inputs.forEach((l, i) => {
      if (l.itemId && Number(l.qty) > 0 && Number(l.qty) > availableFor(l.itemId)) {
        rules[`input_${i}`] = [`Not enough stock for ${itemName(l.itemId)} (available ${availableFor(l.itemId)})`];
      }
    });
    const { errors } = validateForm(rules);
    return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, editId, state]);

  const hasErrors = Object.values(errors).some(e => e);

  const setLine = (group, idx, patch) =>
    setForm(f => ({ ...f, [group]: f[group].map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = (group) => setForm(f => ({ ...f, [group]: [...f[group], emptyLine()] }));
  const removeLine = (group, idx) =>
    setForm(f => ({ ...f, [group]: f[group].length > 1 ? f[group].filter((_, i) => i !== idx) : f[group] }));

  const cleanLines = (lines) => lines
    .filter(l => l.itemId && Number(l.qty) > 0)
    .map(l => ({ itemId: l.itemId, qty: Number(l.qty), uom: getItem(l.itemId)?.unit || '' }));

  const prefixMissing = !editId && !hasPrefix(state, 'production', form.date);

  const handleSave = () => {
    setSubmitted(true);
    if (prefixMissing) { alert('No Prefix Setting found for Production. Add one in Masters → Prefix Setting (effective on the selected date) before saving.'); return; }
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = { date: form.date, batchNo: form.batchNo, narration: form.narration, inputs: cleanLines(form.inputs), outputs: cleanLines(form.outputs) };
      if (editId) {
        dispatch({ type: 'UPDATE_PRODUCTION', payload: { id: editId, ...payload } });
        addToast('Production entry updated', 'success');
      } else {
        dispatch({ type: 'ADD_PRODUCTION', payload });
        addToast('Production entry saved', 'success');
      }
      closeForm();
    } catch (e) {
      addToast('Failed to save production entry', 'error');
      setSaving(false);
    }
  };

  const openNew = () => { setEditId(null); setForm(emptyForm()); setSubmitted(false); setShowForm(true); };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      date: row.date,
      batchNo: row.batchNo || '',
      narration: row.narration || '',
      inputs: (row.inputs || []).length ? row.inputs.map(l => ({ ...l })) : [emptyLine()],
      outputs: (row.outputs || []).length ? row.outputs.map(l => ({ ...l })) : [emptyLine()],
    });
    setSubmitted(false); setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditId(null); setForm(emptyForm()); setSubmitted(false); setSaving(false); };

  const summarize = (lines) => (lines || []).map(l => `${itemName(l.itemId)} × ${l.qty}`).join(', ');

  const columns = [
    { key: 'productionNo', label: 'Production No', sortable: true },
    { key: 'date', label: 'Date', render: v => formatDate(v), exportValue: r => formatDate(r.date) },
    { key: 'inputs', label: 'Inputs (Consumed)', render: v => summarize(v), exportValue: r => summarize(r.inputs) },
    { key: 'outputs', label: 'Outputs (Produced)', render: v => summarize(v), exportValue: r => summarize(r.outputs) },
    { key: 'narration', label: 'Narration' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Production</h1>
        {tab === 'entry' && (
          <button onClick={openNew} className="btn btn-primary flex items-center gap-2">
            <Plus size={16} /> New Production
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button onClick={() => setTab('entry')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'entry' ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Production Entry</button>
      </div>

      {tab === 'entry' && (
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.productions].reverse()}
          searchFields={['productionNo', 'narration']}
          exportFilename="production-entries"
          emptyState={{ title: 'No production entries yet', description: 'Record your first production (consume raw → produce finished).', actionLabel: 'New Production', onAction: openNew }}
          actions={(row) => (
            <>
              <button onClick={() => setPrintDoc(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Pencil size={15} /></button>
              <button onClick={() => {
                if (window.confirm(`Delete production ${row.productionNo}? Stock effects will be reversed.`)) {
                  dispatch({ type: 'DELETE_PRODUCTION', payload: row.id });
                  addToast('Production entry deleted', 'success');
                }
              }} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>
      )}

      <Modal isOpen={showForm} onClose={closeForm} title={editId ? 'Edit Production' : 'New Production'} size="lg">
        <ErrorSummary errors={errors} show={submitted && hasErrors} />

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Production No">
              <input className="input bg-gray-100 font-semibold text-blue-700" readOnly
                value={editId ? state.productions.find(p => p.id === editId)?.productionNo : previewDocNo(state, 'production', 'productions', form.date, 'PRD')} placeholder="Set Prefix first" />
            </FormField>
            <FormField label="Date" required error={errors.date} touched={submitted}>
              <input className="input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} max={today()} />
            </FormField>
            <FormField label="Batch No">
              <input className="input" value={form.batchNo} onChange={e => setForm({ ...form, batchNo: e.target.value })} maxLength={40} placeholder="e.g. B-2026-001" />
            </FormField>
          </div>

          <LineSection
            title="Inputs — Raw Materials Consumed"
            group="inputs"
            lines={form.inputs}
            items={rawItems}
            getItem={getItem}
            error={errors.inputs}
            lineErrors={errors}
            show={submitted}
            onChange={setLine}
            onAdd={() => addLine('inputs')}
            onRemove={(i) => removeLine('inputs', i)}
            showStock
            emptyHint={rawItems.length === 0 ? 'No items are categorised as "Raw Material". Set an item’s Category to include "Raw" in Item Master.' : undefined}
          />

          <LineSection
            title="Outputs — Finished Goods Produced"
            group="outputs"
            lines={form.outputs}
            items={outputItems}
            getItem={getItem}
            error={errors.outputs}
            show={submitted}
            onChange={setLine}
            onAdd={() => addLine('outputs')}
            onRemove={(i) => removeLine('outputs', i)}
          />

          <FormField label="Narration">
            <input className="input" value={form.narration} onChange={e => setForm({ ...form, narration: e.target.value })} maxLength={200} placeholder="e.g. Assembled kit from components" />
          </FormField>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeForm} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={(submitted && hasErrors) || saving}>Save</button>
        </div>
      </Modal>

      {printDoc && (
        <PrintPreview type="production" data={printDoc} onClose={() => setPrintDoc(null)} />
      )}
    </div>
  );
}

function LineSection({ title, group, lines, items, getItem, error, lineErrors = {}, show, onChange, onAdd, onRemove, showStock, emptyHint }) {
  const errKey = (i) => `${group === 'inputs' ? 'input' : 'output'}_${i}`;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="label mb-0">{title}</label>
        <button type="button" onClick={onAdd} className="btn btn-secondary btn-sm flex items-center gap-1"><Plus size={13} /> Add Row</button>
      </div>
      {show && error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {emptyHint && <p className="text-xs text-amber-600 mb-2">{emptyHint}</p>}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Item</th>
              {showStock && <th className="text-right" style={{ width: 110 }}>Stock</th>}
              <th className="text-right" style={{ width: 110 }}>Qty</th>
              <th style={{ width: 80 }}>UOM</th>
              <th style={{ width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const sel = getItem(l.itemId);
              return (
                <tr key={i}>
                  <td>
                    <select className="input" value={l.itemId} onChange={e => onChange(group, i, { itemId: e.target.value })}>
                      <option value="">-- Select Item --</option>
                      {items.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                    {show && lineErrors[errKey(i)] && (
                      <p className="text-xs text-red-600 mt-1">{lineErrors[errKey(i)]}</p>
                    )}
                  </td>
                  {showStock && (
                    <td className="text-right text-gray-600">{sel ? `${sel.currentStock} ${sel.unit || ''}` : '-'}</td>
                  )}
                  <td>
                    <input className="input text-right" type="number" min="0" value={l.qty}
                      onChange={e => onChange(group, i, { qty: Number(e.target.value) })} />
                  </td>
                  <td className="text-gray-600">{sel?.unit || '-'}</td>
                  <td className="text-center">
                    <button type="button" onClick={() => onRemove(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Remove row"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
